import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Filter, Download, FileText, Trash2 } from 'lucide-react';
import { EditableCell, SortableHeader, StatusPill, downloadExport } from './PricingShared';

const BookingsTab = ({ bookings, sortConfig, onSort, bookingView, setBookingView, selectedMonth, setSelectedMonth, onDeleteBooking, onInlineEdit, onShowForm, API }) => {
  const [editingBooking, setEditingBooking] = useState(null);
  const [editingField, setEditingField] = useState(null);

  const handleStartEdit = (bookingId, field) => { setEditingBooking(bookingId); setEditingField(field); };
  const handleSave = (bookingId, field, value) => {
    onInlineEdit(bookingId, field, value);
    setEditingBooking(null);
    setEditingField(null);
  };

  return (
    <Card className="border-gray-200 bg-white">
      <div className="p-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Filter className="h-5 w-5 text-gray-400" />
          <Select value={bookingView} onValueChange={setBookingView} data-testid="booking-filter">
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bookings</SelectItem>
              <SelectItem value="red_zone">Red Zone Only</SelectItem>
              <SelectItem value="exotics_only">Exotics Only</SelectItem>
              <SelectItem value="affiliate_only">Affiliate Only</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
            </SelectContent>
          </Select>
          <Input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="w-40" data-testid="month-selector" />
          <span className="text-sm text-gray-500">{bookings.length} bookings</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadExport(`${API}/api/pricing/export/bookings.csv?month=${encodeURIComponent(selectedMonth)}&filter=${encodeURIComponent(bookingView)}`)} data-testid="export-bookings-csv">
            <Download className="h-4 w-4 mr-1" />CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadExport(`${API}/api/pricing/export/bookings.pdf?month=${encodeURIComponent(selectedMonth)}&filter=${encodeURIComponent(bookingView)}`)} data-testid="export-bookings-pdf">
            <FileText className="h-4 w-4 mr-1" />PDF
          </Button>
          <Button onClick={onShowForm} className="bg-red-600 hover:bg-red-700" data-testid="add-booking-btn">
            <Plus className="h-4 w-4 mr-2" />Add Booking
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="bookings-table">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader label="ID" field="booking_id" sortConfig={sortConfig} onSort={onSort} className="text-left" />
              <SortableHeader label="Date" field="date" sortConfig={sortConfig} onSort={onSort} className="text-left" />
              <SortableHeader label="Service" field="service_type" sortConfig={sortConfig} onSort={onSort} className="text-left" />
              <SortableHeader label="Vehicle" field="vehicle_name" sortConfig={sortConfig} onSort={onSort} className="text-left" />
              <SortableHeader label="Hours" field="hours" sortConfig={sortConfig} onSort={onSort} className="text-right" />
              <SortableHeader label="Pricing" field="pricing_type" sortConfig={sortConfig} onSort={onSort} className="text-left" />
              <SortableHeader label="$/Hr" field="retail_hourly" sortConfig={sortConfig} onSort={onSort} className="text-right" />
              <SortableHeader label="Revenue" field="revenue_total" sortConfig={sortConfig} onSort={onSort} className="text-right" />
              <SortableHeader label="Profit" field="profit_after_driver" sortConfig={sortConfig} onSort={onSort} className="text-right" />
              <SortableHeader label="Margin" field="margin_percent" sortConfig={sortConfig} onSort={onSort} className="text-right" />
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 text-center">Status</th>
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 text-center w-16">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {bookings.map((booking) => (
              <tr key={booking.booking_id} className="hover:bg-gray-50 transition-colors" data-testid={`booking-row-${booking.booking_id}`}>
                <td className="py-2 px-4 text-sm font-mono text-gray-700">{booking.booking_id}</td>
                <td className="py-2 px-4 text-sm">
                  <EditableCell value={booking.date} field="date" type="date" isEditing={editingBooking === booking.booking_id && editingField === 'date'} onStartEdit={(f) => handleStartEdit(booking.booking_id, f)} onSave={(f, v) => handleSave(booking.booking_id, f, v)} />
                </td>
                <td className="py-2 px-4 text-sm capitalize">
                  <EditableCell value={booking.service_type} field="service_type" type="select" options={[{value:'concierge',label:'Concierge'},{value:'group',label:'Group'},{value:'exotic',label:'Exotic'}]} isEditing={editingBooking === booking.booking_id && editingField === 'service_type'} onStartEdit={(f) => handleStartEdit(booking.booking_id, f)} onSave={(f, v) => handleSave(booking.booking_id, f, v)} />
                </td>
                <td className="py-2 px-4 text-sm">
                  <EditableCell value={booking.vehicle_name} field="vehicle_name" type="text" isEditing={editingBooking === booking.booking_id && editingField === 'vehicle_name'} onStartEdit={(f) => handleStartEdit(booking.booking_id, f)} onSave={(f, v) => handleSave(booking.booking_id, f, v)} />
                </td>
                <td className="py-2 px-4 text-sm text-right">
                  <EditableCell value={booking.hours} field="hours" type="number" isEditing={editingBooking === booking.booking_id && editingField === 'hours'} onStartEdit={(f) => handleStartEdit(booking.booking_id, f)} onSave={(f, v) => handleSave(booking.booking_id, f, v)} />
                </td>
                <td className="py-2 px-4 text-sm">
                  <EditableCell value={booking.pricing_type} field="pricing_type" type="select" options={[{value:'retail',label:'Retail'},{value:'affiliate',label:'Affiliate'},{value:'member',label:'Member'}]} isEditing={editingBooking === booking.booking_id && editingField === 'pricing_type'} onStartEdit={(f) => handleStartEdit(booking.booking_id, f)} onSave={(f, v) => handleSave(booking.booking_id, f, v)} />
                </td>
                <td className="py-2 px-4 text-sm text-right">
                  <EditableCell value={booking.pricing_type === 'affiliate' ? booking.affiliate_hourly : booking.retail_hourly} field={booking.pricing_type === 'affiliate' ? 'affiliate_hourly' : 'retail_hourly'} type="number" isEditing={editingBooking === booking.booking_id && (editingField === 'retail_hourly' || editingField === 'affiliate_hourly')} onStartEdit={(f) => handleStartEdit(booking.booking_id, f)} onSave={(f, v) => handleSave(booking.booking_id, f, v)} />
                </td>
                <td className="py-2 px-4 text-sm text-right font-semibold text-green-700">${booking.revenue_total}</td>
                <td className="py-2 px-4 text-sm text-right font-semibold text-blue-700">${booking.profit_after_driver}</td>
                <td className="py-2 px-4 text-sm text-right">{booking.margin_percent}%</td>
                <td className="py-2 px-4 text-center"><StatusPill status={booking.status} /></td>
                <td className="py-2 px-4 text-center">
                  <Button variant="ghost" size="sm" onClick={() => onDeleteBooking(booking.booking_id)} className="hover:bg-red-50" data-testid={`delete-booking-${booking.booking_id}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {bookings.length === 0 && <div className="text-center py-12 text-gray-500">No bookings found. Click "Add Booking" to create one.</div>}
      </div>
    </Card>
  );
};

export default BookingsTab;
